import React, {useState} from "react";
import { Alert } from "@mui/material";
import Table from "@mui/material/Table";
import { Edit } from "@mui/icons-material";
import TableRow from "@mui/material/TableRow";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import IconButton from "@mui/material/IconButton";
import TableSortLabel from "@mui/material/TableSortLabel";
import TableContainer from "@mui/material/TableContainer";
import LinearProgress from "@mui/material/LinearProgress";
import TablePagination from "@mui/material/TablePagination";
import { SIZES, ALIGN, HTML_TAGS, SORT_ORDERS, STATUS } from "../../common/constants/commonConstants.js";
import { MESSAGE_TABLE_COLUMNS, MESSAGE_TABLE_COLUMN_KEYS, MESSAGE_MESSAGES, MESSAGE_ERRORS } from "../../common/constants/scheduleModule/messagesConstants.js";
import { updateScheduleMessage } from "../../services/scheduleModule/scheduledataServices.js";
import { useClientTimeZone, formatDateInZone, zoneAbbr } from "../../utils/clienttimezone";
import EditMessageModal from "./modals/EditMessageModal.jsx";

const UpcomingScheduleMessagesTable = ({
	page,
	order,
	setPage,
	orderBy,
	inactive,
	rowsPerPage,
	isLoading,
	messages,
	fetchMessages,
	setRowsPerPage,
	handleRequestSort,
	filteredMessagesLength,
}) => {
	const [successMessage, setSuccessMessage] = useState("");
	const [errorMessage, setErrorMessage] = useState("");
	const [selectedMessage, setSelectedMessage] = useState("");
	const [isEditMessageModalOpen, setIsEditMessageModalOpen] = useState(false);

	// Send times render in the configured client zone, not the browser's.
	const { timeZone } = useClientTimeZone();
	const tzLabel = zoneAbbr(timeZone);

	const handleEditMessage = (message = {}) => {
        setSelectedMessage(message);
        setIsEditMessageModalOpen(true);
    };	
	
	const handleInputChange = (field) => (e) =>
		setSelectedMessage({ ...selectedMessage, [field]: e.target.value });

	const handleSaveMessage = async () => {
		if (!selectedMessage) return;
		try {
			await updateScheduleMessage(selectedMessage);
			setSuccessMessage(MESSAGE_MESSAGES.MESSAGE_UPDATED);
			setIsEditMessageModalOpen(false);
			await fetchMessages();
			setTimeout(() => setSuccessMessage(""), 3000);
		} catch (error) {
			console.error(MESSAGE_ERRORS.FETCH_MESSAGES_ERROR, error);
			setErrorMessage(MESSAGE_ERRORS.MESSAGE_UPDATE_FAILED);
			setTimeout(() => setErrorMessage(""), 3000);
		}
	};

	return (
		<>
			{successMessage &&
				<Alert severity={STATUS.SUCCESS} sx={{ mb: 2, justifyContent: "center" }}>
					{successMessage}
				</Alert>
			}
			{errorMessage &&
				<Alert severity={STATUS.ERROR} sx={{ mb: 2, justifyContent: "center" }}>
					{errorMessage}
				</Alert>
			}
			<TableContainer sx={{ mt: 2 }}>
				<Table size={SIZES.SMALL}>
					<TableHead>
						<TableRow>
							{MESSAGE_TABLE_COLUMNS.map((column) => {
								const sortId = column.sortKey ?? column.id;
								const label = column.id === MESSAGE_TABLE_COLUMN_KEYS.MESSAGE_DATE
									? `${column.label} (${tzLabel})`
									: column.label;
								return (
									<TableCell
										key={column.id}
										sortDirection={orderBy === sortId ? order : false}
										sx={{
											width:
												column.id === MESSAGE_TABLE_COLUMN_KEYS.ACTIONS ? 40 :
												column.id === MESSAGE_TABLE_COLUMN_KEYS.MESSAGE_DATE ? 150 :
												column.id === MESSAGE_TABLE_COLUMN_KEYS.CUSTOMER ? 150 :
												column.id === MESSAGE_TABLE_COLUMN_KEYS.MESSAGE ? 500 :
												column.id === MESSAGE_TABLE_COLUMN_KEYS.CHANNEL ? 100 : 100,
										}}
									>
										{column.isSortable ? (
											<TableSortLabel
												active={orderBy === sortId}
												direction={orderBy === sortId ? order : SORT_ORDERS.ASCENDING}
												onClick={() => handleRequestSort(sortId)}
											>
												{label}
											</TableSortLabel>
										) : (
											label
										)}
									</TableCell>
								);
							})}
						</TableRow>
					</TableHead>
					<TableBody>
						{isLoading ? (
							<TableRow>
								<TableCell colSpan={MESSAGE_TABLE_COLUMNS.length} align={ALIGN.CENTER}>
									<LinearProgress />
								</TableCell>
							</TableRow>
						) : messages.length > 0 ? (
							messages.map((message) => (
								<TableRow hover tabIndex={-1} key={message.ID}>
									<TableCell>
										<IconButton
											size={SIZES.SMALL}
											onClick={() =>
												handleEditMessage(message)
											}
											disabled={inactive}
										>
											<Edit fontSize={SIZES.SMALL} />
										</IconButton>
									</TableCell>
									<TableCell>
										{formatDateInZone(message.msg_scheduled_at, timeZone)}
									</TableCell>
									<TableCell>
										{message.customer}
									</TableCell>
									<TableCell>
										{message.campaignSchedulemsg}
									</TableCell>
									<TableCell>
										{message.channeltype}
									</TableCell>
								</TableRow>
							))
						) : (
							<TableRow>
								<TableCell colSpan={MESSAGE_TABLE_COLUMNS.length} align={ALIGN.CENTER}>
									No records found.
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
				<TablePagination
					page={page}
					rowsPerPage={rowsPerPage}
					component={HTML_TAGS.DIV}
					count={filteredMessagesLength}
					rowsPerPageOptions={[10, 25, 50]}
					onPageChange={(event, newPage) => setPage(newPage)}
					onRowsPerPageChange={(event) => {
						setPage(0);
						setRowsPerPage(parseInt(event.target.value, 10));
					}}
				/>
			</TableContainer>
			<EditMessageModal
				onSave={handleSaveMessage}
				open={isEditMessageModalOpen}
				selectedMessage={selectedMessage}
				handleInputChange={handleInputChange}
				onClose={() => setIsEditMessageModalOpen(false)}
			/>
		</>
	);
};

export default UpcomingScheduleMessagesTable;